export const en = {
  // Navigation
  navigation: {
    singleVerification: 'Single Verification',
    batchVerification: 'Batch Verification',
    about: 'About',
    logout: 'Logout'
  },
  auth: {
    emailVerification: {
      title: 'Verify Your Email',
      message: 'We\'ve sent a verification email to {email}. Please check your inbox and click the verification link to activate your account.',
      checkEmail: 'Check your email',
      followInstructions: 'Follow the instructions in the email to verify your account.',
      resendEmail: 'Resend Verification Email',
      sending: 'Sending...',
      backToLogin: 'Back to Login',
      emailSent: 'Verification email sent successfully!',
      helpText: 'If you don\'t see the email in your inbox, please check your spam folder.'
    }
  },

  // Header
  header: {
    title: 'Peppol VAT Verification',
    subtitle: 'EU VAT & Peppol Verification Tool'
  },

  // Footer
  footer: {
    description: 'Peppol VAT Verification Tool - Built with React & TypeScript',
    disclaimer: 'For demonstration purposes only. Production use requires proper API integration.',
    termsOfUse: 'Terms of Use',
    privacyPolicy: 'Privacy Policy'
  },

  // About Page
  about: {
    title: 'About Peppol VAT Verification',
    overview: {
      title: 'Overview',
      content: 'This application provides verification services for EU VAT numbers and Peppol participant IDs. It supports both single company verification and batch processing for multiple entries.'
    },
    features: {
      title: 'Features',
      items: [
        { label: 'EU VAT Verification:', description: 'Validate VAT numbers using VIES (VAT Information Exchange System)' },
        { label: 'Peppol Verification:', description: 'Check participant IDs against the Peppol directory' },
        { label: 'Dual Verification:', description: 'Run both VAT and Peppol verification with a single identifier' },
        { label: 'Single Verification:', description: 'Verify individual companies with detailed results' },
        { label: 'Batch Processing:', description: 'Upload CSV files for bulk verification' },
        { label: 'Real API Integration:', description: 'Toggle between real API calls and simulation mode' },
        { label: 'Export Results:', description: 'Download verification results as CSV files' },
        { label: 'Template Downloads:', description: 'Get CSV templates for proper data formatting' }
      ]
    },
    vatFormats: {
      title: 'Supported VAT Number Formats',
      description: 'The application supports VAT numbers from all EU member states:',
      examples: [
        'Austria: U12345678',
        'Belgium: 1234567890',
        'Germany: 123456789',
        'France: AB123456789',
        'Italy: 12345678901',
        'Netherlands: 123456789B01',
        'Spain: A12345678',
        'And many more...'
      ]
    },
    peppolTypes: {
      title: 'Peppol Participant ID Types',
      items: [
        { type: 'DUNS:', description: '9-digit Data Universal Numbering System' },
        { type: 'GLN:', description: '12-15 digit Global Location Number' },
        { type: 'VAT:', description: 'Country code + VAT number' },
        { type: 'Custom:', description: 'Organization-specific identifiers' }
      ]
    },
    apiMode: {
      title: 'API Mode Toggle',
      description: 'The application includes a toggle switch to choose between:',
      items: [
        { mode: 'Simulation Mode:', description: 'Uses mock data for demonstration purposes' },
        { mode: 'Real API Mode:', description: 'Makes actual calls to VIES and Peppol APIs' }
      ]
    },
    importantNotes: {
      title: 'Important Notes',
      items: [
        'Real API mode requires proper network access to VIES and Peppol services',
        'Some APIs may have rate limiting or require authentication',
        'Simulation mode is recommended for testing and demonstration',
        'Production use should include proper error handling and retry logic'
      ]
    },
    technicalDetails: {
      title: 'Technical Details',
      content: 'Built with React, TypeScript, and Tailwind CSS. Uses Papa Parse for CSV processing and includes comprehensive error handling and user feedback.'
    }
  },

  // Terms of Use
  termsOfUse: {
    title: 'Terms of Use – vateu.eu',
    sections: {
      scope: {
        title: '1. Scope and Beta Status',
        content: 'The Service is provided free of charge for evaluation purposes only. It is currently offered to selected testers, including Shell, to assess quality and effectiveness before any potential commercial release. No production use is recommended. The Service may be modified, suspended, or discontinued at any time without notice.'
      },
      dataSources: {
        title: '2. Data Sources',
        content: 'VAT validations are performed using the EU Commission\'s VIES system and connected national databases. Peppol validations are based on public Peppol Directory / SMP sources. We are not affiliated with the European Commission, any national tax authority, or OpenPeppol.'
      },
      accuracy: {
        title: '3. Accuracy and Availability',
        content: 'Information may be delayed, incomplete, or unavailable due to third-party system downtime. The Service is provided "as is", without warranties of any kind. No rights or obligations may be derived from validation results.'
      },
      acceptableUse: {
        title: '4. Acceptable Use',
        content: 'Users must not abuse the Service through excessive automated requests. Bulk queries are subject to rate-limits and may be blocked. The Service must not be used for unlawful purposes.'
      },
      intellectualProperty: {
        title: '5. Intellectual Property',
        content: 'The content, code, and design of vateu.eu are owned by Mariusz Pietruszka (PL6572490600) "VIES" and "Peppol" are names of third parties; we do not claim rights in them and use them descriptively only. Do not use the EU emblem or Peppol logos in connection with this Service.'
      },
      privacy: {
        title: '6. Privacy and Data Handling',
        content: 'The Service does not permanently store any VAT numbers, Peppol identifiers, or validation results submitted by users. Input data is transmitted only to the relevant third-party validation systems (e.g. VIES, Peppol Directory) and processed solely to generate the validation result displayed to you. After the result is returned, no copy of the input data or validation output is retained in our system, except for short-term technical logs (see Privacy Policy).'
      },
      liability: {
        title: '7. Liability',
        content: 'To the fullest extent permitted by law, we disclaim any warranties, express or implied. We accept no liability for direct, indirect, or consequential damages resulting from use of the Service.'
      },
      changes: {
        title: '8. Changes',
        content: 'We may update these Terms at any time. Continued use constitutes acceptance of the revised Terms.'
      },
      governingLaw: {
        title: '9. Governing Law',
        content: 'These Terms are governed by the laws of Poland. Any disputes shall be subject to the exclusive jurisdiction of the courts of Poland.',
        contact: 'Contact: Mariusz Pietruszka – PL6572490600'
      }
    }
  },

  // Privacy Policy
  privacyPolicy: {
    title: 'Privacy Policy – vateu.eu',
    effectiveDate: 'Effective date: 14/09/2025',
    sections: {
      controller: {
        title: '1. Controller',
        content: 'The Service is operated by: [Your company name, registered address, contact email] For privacy inquiries, contact: [privacy@vateu.eu].'
      },
      dataWeProcess: {
        title: '2. Data We Process',
        inputData: 'Input data: VAT numbers and Peppol identifiers you submit for validation are processed only in memory and transmitted to VIES or the Peppol Directory. We do not store these identifiers or the validation results.',
        logs: 'Logs: minimal technical data (IP address, browser type, timestamp, error logs) may be collected to ensure security and troubleshoot issues.',
        optionalContact: 'Optional contact: if you reach out via email or form, we process your contact details and message.'
      },
      purposeAndLegalBasis: {
        title: '3. Purpose and Legal Basis',
        items: [
          'To provide validation results (our legitimate interest, Art. 6(1)(f) GDPR).',
          'To prevent abuse and ensure system stability (legitimate interest).',
          'To communicate with you if you contact us (performance of a contract, Art. 6(1)(b) GDPR).'
        ]
      },
      dataRetention: {
        title: '4. Data Retention',
        items: [
          'VAT/Peppol IDs and validation results are not stored after the validation request is completed.',
          'Logs are kept for a maximum of [e.g. 30 days] and then deleted or anonymised.',
          'Contact data is retained as long as needed to answer your request.'
        ]
      },
      dataSharing: {
        title: '5. Data Sharing',
        items: [
          'Validation requests are transmitted to VIES (European Commission) and/or Peppol Directory systems.',
          'We do not sell or share personal data with third parties for marketing purposes.',
          'Hosting and infrastructure providers may process data on our behalf under data-processing agreements.'
        ]
      },
      internationalTransfers: {
        title: '6. International Transfers',
        content: 'Data may be processed on servers located in the EU/EEA. If transfers outside the EU/EEA occur, we ensure appropriate safeguards (e.g. Standard Contractual Clauses).'
      },
      yourRights: {
        title: '7. Your Rights',
        description: 'You have rights under GDPR, including:',
        items: [
          'Access to your data',
          'Rectification or erasure',
          'Restriction or objection to processing',
          'Data portability',
          'Lodge a complaint with your supervisory authority'
        ]
      },
      cookiesAnalytics: {
        title: '8. Cookies & Analytics',
        content: 'We use only essential cookies for service functionality. If we introduce analytics or non-essential cookies, you will be asked for consent.'
      },
      changes: {
        title: '9. Changes',
        content: 'We may update this Privacy Policy. The latest version will always be available on the site.'
      },
      contact: {
        title: '10. Contact',
        content: 'For questions or requests regarding this Privacy Policy, email [privacy@vateu.eu].'
      }
    }
  },

  // Single Verification
  singleVerification: {
    title: 'Single Verification',
    verificationType: 'Verification Type',
    types: {
      vat: 'EU VAT Verification',
      peppol: 'Peppol Verification',
      both: 'Both Verifications'
    },
    forms: {
      vatNumber: 'VAT Number',
      vatNumberPlaceholder: 'e.g., DE123456789',
      peppolId: 'Peppol Participant ID',
      peppolIdPlaceholder: 'e.g., 123456789 (DUNS) or 1234567890123 (GLN)',
      peppolIdHelp: 'Enter a DUNS number, GLN, VAT number, or custom participant ID',
      companyIdentifier: 'Company Identifier',
      companyIdentifierPlaceholder: 'e.g., DE123456789, 123456789, or 1234567890123',
      companyIdentifierHelp: 'Enter a VAT number (DE123456789), DUNS (123456789), GLN (1234567890123), or any other identifier',
      unifiedTitle: 'Unified Verification',
      unifiedDescription: 'Enter a single identifier that will be used for both VAT and Peppol verification. This can be a VAT number, DUNS, GLN, or any other company identifier.'
    },
    buttons: {
      verify: 'Verify',
      verifying: 'Verifying...',
      reset: 'Reset'
    },
    apiMode: {
      simulationMode: 'Simulation Mode',
      realAPI: 'Real API',
      simulation: 'Simulation'
    },
    results: {
      verificationSuccessful: 'Verification Successful',
      verificationFailed: 'Verification Failed',
      verificationResults: 'Verification Results',
      vatVerification: 'VAT Verification',
      peppolVerification: 'Peppol Verification',
      fields: {
        vatNumber: 'VAT Number',
        companyName: 'Company Name',
        address: 'Address',
        fullParticipantId: 'Full Participant ID',
        participantName: 'Participant Name',
        participantId: 'Participant ID',
        scheme: 'Scheme',
        supportedDocumentTypes: 'Supported Document Types',
        supportsBilling: 'Supports Billing',
        error: 'Error',
        verifiedAt: 'Verified at',
        yes: 'Yes',
        no: 'No'
      },
      multipleMatches: {
        title: 'Multiple Matches Found',
        matchCount: 'matches',
        match: 'Match'
      }
    }
  },

  // Batch Verification
  batchVerification: {
    title: 'Batch Verification',
    inputMethod: 'Input Method',
    methods: {
      csv: 'CSV Upload',
      textarea: 'Text Input'
    },
    forms: {
      uploadFile: 'Upload CSV File',
      downloadTemplate: 'Download CSV Template',
      textAreaPlaceholder: 'Enter identifiers separated by commas or new lines (e.g., DE123456789, 123456789, 1234567890123)',
      textAreaHelp: 'Enter one or more identifiers per line. Separate multiple identifiers on the same line with commas.',
      textInputTitle: 'For text input, enter identifiers separated by commas:',
      textInputDescription: 'Enter identifiers separated by commas. Each line can contain multiple identifiers. Supports VAT numbers, DUNS, GLN, and other company identifiers.',
      textInputRules: [
        'Each line can contain multiple identifiers separated by commas',
        'Supports VAT numbers (DE123456789), DUNS (123456789), GLN (1234567890123)',
        'Empty lines are ignored',
        'Whitespace around identifiers is automatically trimmed'
      ],
      dualVerificationHelp: 'The identifier can be a VAT number, DUNS, GLN, or any other company identifier that works for both VAT and Peppol verification.',
      identifiersReady: 'identifiers ready for verification',
      exportResults: 'Export results',
      textInputPlaceholder: 'Enter identifiers separated by commas. Each line can contain multiple identifiers:\n\nDE123456789, FR12345678901\n123456789, 987654321\n1234567890123',
      textInputHelp: 'Enter identifiers separated by commas. Each line can contain multiple identifiers. Supports VAT numbers, DUNS, GLN, and other company identifiers.'
    },
    buttons: {
      process: 'Process Verification',
      processing: 'Processing...',
      downloadResults: 'Download Results',
      reset: 'Reset',
      cancel: 'Cancel'
    },
    results: {
      summary: 'Verification Summary',
      totalProcessed: 'Total Processed',
      valid: 'Valid',
      invalid: 'Invalid',
      errors: 'Errors',
      processingTime: 'Processing Time',
      noResults: 'No results to display',
      exportResults: 'Export Results',
      vatVerification: 'VAT Verification',
      dualVerificationResults: 'Dual Verification Results'
    },
    progress: {
      processing: 'Processing verification...',
      completed: 'Processing completed'
    },
    format: {
      csvRequirements: 'CSV Format Requirements',
      textInputFormat: 'Text Input Format',
      dualVerificationTitle: 'For dual verification, include these columns:',
      dualVerificationColumns: [
        'identifier or id - Company identifier (required)',
        'Alternative column names: vatNumber, vat, taxId, participantId, peppolId'
      ],
      vatVerificationTitle: 'For VAT verification, include these columns:',
      vatVerificationColumns: [
        'vatNumber or vat or taxId - VAT number (required)'
      ],
      peppolVerificationTitle: 'For Peppol verification, include these columns:',
      peppolVerificationColumns: [
        'participantId or peppolId or id - Participant ID (required)'
      ]
    }
  },

  // Error Messages
  errors: {
    general: {
      unknownError: 'Unknown error occurred',
      networkError: 'Network error occurred',
      parsingError: 'Error parsing CSV file. Please check the format.',
      noDataToProcess: 'Please upload a CSV file or enter identifiers in the text area.',
      invalidInput: 'Invalid input provided'
    },
    vat: {
      invalidFormat: 'Invalid VAT number format',
      countryNotSupported: 'Country not supported by VIES',
      serviceUnavailable: 'VIES service is currently unavailable',
      timeout: 'Request timeout - VIES service may be slow',
      rateLimit: 'Rate limit exceeded - please try again later'
    },
    peppol: {
      invalidFormat: 'Invalid Peppol participant ID format',
      serviceUnavailable: 'Peppol service is currently unavailable',
      timeout: 'Request timeout - Peppol service may be slow',
      notFound: 'Participant not found in Peppol directory',
      rateLimit: 'Rate limit exceeded - please try again later'
    },
    serviceUnavailable: {
      title: 'Service Unavailable',
      message: 'The {service} service is currently unavailable due to high demand or maintenance. We will automatically retry your request.',
      autoRetryIn: 'Automatic retry in:',
      retryNow: 'Retry Now',
      cancel: 'Cancel',
      helpText: 'You can try again manually or wait for the automatic retry. The service will be restored as soon as possible.'
    }
  }
};
